import { useState, useEffect, useCallback } from "react";
import { Accessibility, X, Type, Contrast, RotateCcw, Check } from "lucide-react";

const KEY = "tt-accessibility";

export const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [textSize, setTextSize] = useState("normal"); // normal | large | xlarge
  const [contrast, setContrast] = useState(false);

  // Load saved preferences
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
      if (saved.textSize) setTextSize(saved.textSize);
      if (saved.contrast) setContrast(true);
    } catch (err) {
      console.warn("Could not read accessibility preferences.", err);
    }
  }, []);

  // Apply preferences to <html>
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("tt-large", "tt-xlarge");
    if (textSize === "large") html.classList.add("tt-large");
    if (textSize === "xlarge") html.classList.add("tt-xlarge");
    html.classList.toggle("tt-contrast", contrast);
    try {
      localStorage.setItem(KEY, JSON.stringify({ textSize, contrast }));
    } catch (err) {
      console.warn("Could not save accessibility preferences.", err);
    }
  }, [textSize, contrast]);

  const reset = useCallback(() => {
    setTextSize("normal");
    setContrast(false);
  }, []);

  const sizes = [
    { key: "normal", label: "Normal" },
    { key: "large", label: "Large" },
    { key: "xlarge", label: "Largest" },
  ];

  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3 print:hidden">
      {open && (
        <div
          data-testid="accessibility-panel"
          className="w-[300px] rounded-2xl border border-border bg-card shadow-2xl p-6"
          role="dialog"
          aria-label="Accessibility options"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-bold text-lg text-foreground">Accessibility</h3>
            <button
              data-testid="accessibility-close"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility options"
              className="grid place-items-center h-9 w-9 rounded-full text-muted-foreground hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-foreground font-semibold mb-2">
            <Type className="h-5 w-5 text-accent" /> Text size
          </div>
          <div className="grid grid-cols-3 gap-2 mb-6">
            {sizes.map((s) => (
              <button
                key={s.key}
                data-testid={`text-size-${s.key}`}
                onClick={() => setTextSize(s.key)}
                className={`h-12 rounded-xl font-bold transition-colors ${
                  textSize === s.key
                    ? "bg-accent text-white"
                    : "bg-secondary border border-border text-foreground hover:border-accent"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-foreground font-semibold mb-2">
            <Contrast className="h-5 w-5 text-accent" /> Display
          </div>
          <button
            data-testid="toggle-contrast"
            onClick={() => setContrast((v) => !v)}
            aria-pressed={contrast}
            className={`w-full h-12 rounded-xl font-bold transition-colors inline-flex items-center justify-center gap-2 mb-5 ${
              contrast
                ? "bg-accent text-white"
                : "bg-secondary border border-border text-foreground hover:border-accent"
            }`}
          >
            {contrast && <Check className="h-5 w-5" />} High contrast
          </button>

          <button
            data-testid="accessibility-reset"
            onClick={reset}
            className="w-full h-11 rounded-xl border border-border text-muted-foreground font-semibold inline-flex items-center justify-center gap-2 hover:border-accent hover:text-accent transition-colors"
          >
            <RotateCcw className="h-4 w-4" /> Reset to default
          </button>
        </div>
      )}

      <button
        data-testid="accessibility-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Accessibility options"
        aria-expanded={open}
        className="grid place-items-center h-16 w-16 rounded-full bg-primary text-white shadow-2xl hover:bg-accent transition-colors"
      >
        <Accessibility className="h-8 w-8" />
      </button>
    </div>
  );
};
